import { AsyncPipe, KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PatientAvatarComponent } from '@modules/patient/patient-avatar/patient-avatar.component';
import { PatientDataComponent } from '@modules/patient/patient-data/patient-data.component';
import { PatientSearchListComponent } from '@modules/patient/patient-search-list/patient-search-list.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { TuiButton, TuiDialogContext, TuiNotification, TuiScrollbar } from '@taiga-ui/core';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { Observable, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, shareReplay, startWith, tap } from 'rxjs/operators';
import { PatientHttpService } from 'src/app/services/http/patient.http.service';
import { Size } from 'src/app/shared/models/enums/size.enum';
import { formValidationErrors } from 'src/app/shared/models/form-validation/form-validation-error.config';
import { FormValidationError } from 'src/app/shared/models/form-validation/form-validation-error.enum';
import { selectIsGamEnabled } from 'src/app/shared/store/clients/clients.selectors';
import { comparePatients } from 'src/app/shared/utils/patient-utils';
import { PatientEditFormComponent } from '../patient-edit-form/patient-edit-form.component';
import { PatientEditService } from './patient-edit.service';

@UntilDestroy()
@Component({
  selector: 'pxc-patient-edit',
  templateUrl: './patient-edit.component.html',
  providers: [PatientEditService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslocoDirective,
    PatientAvatarComponent,
    PatientDataComponent,
    TuiNotification,
    KeyValuePipe,
    AsyncPipe,
    PatientEditFormComponent,
    TuiScrollbar,
    PatientSearchListComponent,
    TuiButton,
    TuiButtonLoading,
  ],
})
export class PatientEditComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly patientService = inject(PatientHttpService);
  private readonly patientEditService = inject(PatientEditService);
  private readonly translocoService = inject(TranslocoService);
  private readonly context = inject<
    TuiDialogContext<
      void,
      {
        patient: Patient;
        clientCode: string;
        displayActionButtons?: boolean;
        patientEditionDisabled?: boolean;
      }
    >
  >(POLYMORPHEUS_CONTEXT, { optional: true });

  @Input() patient: Patient = this.context?.data.patient;
  @Input() clientCode: string = this.context?.data.clientCode;
  @Input() displayActionButtons: boolean = this.context?.data.displayActionButtons || true;
  @Input() patientEditionDisabled: boolean = this.context?.data.patientEditionDisabled || false;
  @Output() readonly patientChange = new EventEmitter<Patient>();
  @Output() readonly isCheckingFormValidityChange = new EventEmitter<boolean>();
  @Output() readonly formValidityUpdate = new EventEmitter<boolean>();

  basePatient: Patient;
  patientEditForm: UntypedFormGroup;
  selectedPatient: Patient = null;
  displayPatientsList = false;
  isPatientAnonymous = false;

  selectIsGamEnabled$: Observable<boolean>;
  searchedPatients$: Observable<Patient[]>;
  isCheckingFormValidity$ = new ReplaySubject<boolean>(1);

  requiredFormFieldNames = ['lastName', 'firstName', 'birthDate'];
  FormValidationError = FormValidationError;
  Size = Size;

  ngOnInit(): void {
    this.basePatient = this.patient;
    this.isPatientAnonymous = this.checkPatientAnonymity(this.patient);

    this.selectIsGamEnabled$ = this.store.select(selectIsGamEnabled(this.clientCode || this.patient?.clientCode));

    this.patientEditForm = new UntypedFormGroup(
      {
        id: new UntypedFormControl(this.patient?.id),
        businessIdentifier: new UntypedFormControl(this.patient?.businessIdentifier),
        firstName: new UntypedFormControl(this.patient?.firstName),
        lastName: new UntypedFormControl(this.patient?.lastName),
        birthName: new UntypedFormControl(this.patient?.birthName),
        birthDate: new UntypedFormControl(this.patient?.birthDate),
        isGamLinked: new UntypedFormControl(this.patient?.isGamLinked),
        clientCode: new UntypedFormControl(this.clientCode || this.patient?.clientCode),
        createdBy: new UntypedFormControl(this.patient?.createdBy),
        lastActivity: new UntypedFormControl(this.patient?.lastActivity),
      },
      {
        validators: this.contextualValidators(),
      },
    );

    this.patientEditForm.valueChanges
      .pipe(
        tap(() => {
          this.isCheckingFormValidity$.next(true);
          this.isCheckingFormValidityChange.emit(true);
        }),
        distinctUntilChanged(),
        debounceTime(300),
        untilDestroyed(this),
      )
      .subscribe((values) => {
        this.isPatientAnonymous = this.checkPatientAnonymity(values);
        this.selectedPatient = null;
        this.displayPatientsList = true;
        this.searchedPatients$ = this.patientService
          .search({ ...values, isArchived: false })
          .pipe(shareReplay({ bufferSize: 1, refCount: true }));

        this.searchedPatients$.subscribe((patients) => {
          const sameIdentifierPatient = patients.find(
            (patient) => patient.businessIdentifier === values.businessIdentifier,
          );
          let errorKey = null;
          let isGamLinked = false;
          if (sameIdentifierPatient) {
            if (!PatientEditService.compare(sameIdentifierPatient, values)) {
              if (sameIdentifierPatient.isGamLinked) {
                errorKey = FormValidationError.OVERRIDEN_GAM_PATIENT;
              } else if (!this.basePatient || this.basePatient.businessIdentifier !== values.businessIdentifier) {
                errorKey = FormValidationError.OVERRIDEN_LOCAL_PATIENT;
              }
            } else {
              isGamLinked = sameIdentifierPatient.isGamLinked;
              this.selectedPatient = sameIdentifierPatient;
            }
          }
          this.patientEditForm.patchValue({ isGamLinked }, { emitEvent: false });
          this.updateFormErrors(errorKey);
          this.formValidityUpdate.next(this.patientEditForm.valid && !this.patientEditForm.errors);
          this.emitChanges();
        });
      });
  }

  checkPatientAnonymity(patient: Patient): boolean {
    return (
      !patient || (!patient.businessIdentifier && this.requiredFormFieldNames.every((fieldName) => !patient[fieldName]))
    );
  }

  updateFormErrors(errorKey: FormValidationError): void {
    this.patientEditForm?.setErrors(
      errorKey
        ? {
            [errorKey]: this.translocoService.translate(formValidationErrors[errorKey]),
            ...this.patientEditForm?.errors,
          }
        : this.patientEditForm?.errors,
      { emitEvent: false },
    );
  }

  contextualValidators(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      let isValid = true;
      this.requiredFormFieldNames.forEach((controlName) => {
        const control = formGroup?.get([controlName]);
        let errors = null;
        if (!control.value && this.areSomeFormFieldsFilled(['businessIdentifier'])) {
          isValid = false;
          errors = { errors: { required: true } };
        }
        control?.setErrors(errors, { emitEvent: false });
      });

      return isValid
        ? null
        : {
            [FormValidationError.INVALID]: this.translocoService.translate(
              formValidationErrors[FormValidationError.INVALID],
            ),
          };
    };
  }

  areSomeFormFieldsFilled(additionalFieldNames: string[] = []): boolean {
    return [...this.requiredFormFieldNames, ...additionalFieldNames].some(
      (formFieldName) => !!this.patientEditForm?.getRawValue()[formFieldName],
    );
  }

  selectPatient(patient: Patient): void {
    this.patientEditForm.setValue(
      {
        id: patient.id,
        firstName: patient.firstName,
        lastName: patient.lastName,
        birthName: patient.birthName,
        birthDate: patient.birthDate,
        businessIdentifier: patient.businessIdentifier,
        clientCode: patient.clientCode,
        isGamLinked: patient.isGamLinked,
        createdBy: patient.createdBy,
        lastActivity: patient.lastActivity,
      },
      { emitEvent: false },
    );
    this.selectedPatient = patient;

    this.formValidityUpdate.next(this.patientEditForm.valid && !this.patientEditForm.errors);

    this.emitChanges();
  }

  emitChanges(): void {
    const isFormValid = this.patientEditForm.valid && !this.patientEditForm.errors;
    this.formValidityUpdate.next(isFormValid);
    this.isCheckingFormValidity$.next(false);
    this.isCheckingFormValidityChange.emit(false);

    this.patientChange.emit({
      ...this.patientEditForm.getRawValue(),
      id: this.patient?.id,
      businessIdentifier: this.patientEditForm.getRawValue()?.businessIdentifier || null,
      firstName: this.patientEditForm.getRawValue()?.firstName || null,
      lastName: this.patientEditForm.getRawValue()?.lastName || null,
      birthName: this.patientEditForm.getRawValue()?.birthName || null,
      birthDate: this.patientEditForm.getRawValue()?.birthDate
        ? new Date(this.patientEditForm.getRawValue()?.birthDate)
        : null,
      createdBy: this.patientEditForm.getRawValue()?.createdBy || null,
      lastActivity: this.patientEditForm.getRawValue()?.lastActivity
        ? new Date(this.patientEditForm.getRawValue()?.lastActivity)
        : null,
    });
  }

  save(): void {
    this.isCheckingFormValidity$.pipe(startWith(false), first()).subscribe((isChecking) => {
      if (isChecking) {
        return;
      }

      this.isCheckingFormValidity$.next(true);

      if (
        comparePatients(this.patient, this.patientEditForm.value) &&
        this.patient.businessIdentifier === this.patientEditForm.getRawValue()?.businessIdentifier
      ) {
        this.closeEdition();
        this.isCheckingFormValidity$.next(false);
      } else {
        this.patientEditService
          .updatePatient(this.basePatient, this.patientEditForm)
          .pipe(first())
          .subscribe(() => {
            this.closeEdition();
            this.isCheckingFormValidity$.next(false);
          });
      }
    });
  }

  closeEdition(): void {
    if (this.context) {
      this.context.completeWith();
    }
  }
}
